import { Field, FieldProps } from 'formik';
import React, { ReactNode } from 'react';
import { InjectedIntl, injectIntl } from 'react-intl';
import InputField from '../components/InputField';
import ValueFormatters from './valueFormatters';
import { TranslationHelpers } from '../../../i18n/translationHelpers';
import { AddressLineFieldValidator } from '../validator/addressLineFieldValidator';
import { ValidatorRule } from '../validator/validatorRule.constants';
import { IErrorMessage } from '../components/errorMessage/errorMessage.type';

interface IAddressLineFieldProps {
    autoFocus?: boolean;
    intl: InjectedIntl;
    count: number;
    value?: string;
    required?: boolean;
    validatorRules?: ValidatorRule[];
}

class AddressLineField extends React.Component<IAddressLineFieldProps> {
    private readonly ID: string;
    private readonly LABEL_ID: string;
    private readonly ERROR_REQUIRED_ID: string;
    private readonly MAX_LENGTH: number;

    private addressLineFieldValidator: AddressLineFieldValidator;

    constructor(props: IAddressLineFieldProps) {
        super(props);

        const { count, validatorRules = [] } = this.props;

        this.ID = `addressLine${count}`;
        this.LABEL_ID = 'label.address_line';
        this.ERROR_REQUIRED_ID = 'error.address_line_required';
        this.MAX_LENGTH = 255;

        this.validate = this.validate.bind(this);
        this.renderInputField = this.renderInputField.bind(this);
        this.addressLineFieldValidator = new AddressLineFieldValidator(validatorRules);
    }

    public render() {
        return <Field name={this.ID} validate={this.validate} render={this.renderInputField} />;
    }

    private validate(value: string): IErrorMessage {
        const { intl, required, validatorRules = [] } = this.props;

        if (!value && required) {
            return intl.formatMessage(
                TranslationHelpers.getMessageDescriptor(this.ERROR_REQUIRED_ID),
            );
        }

        let errorMessage: IErrorMessage = '';
        if (value && validatorRules.length > 0) {
            const err = this.addressLineFieldValidator.validate(value, this.LABEL_ID);
            if (err !== null) {
                errorMessage = err.message;
            }
        }

        return errorMessage;
    }

    private renderInputField({
        form: { touched, errors, setFieldValue, handleBlur },
    }: FieldProps): ReactNode {
        const { autoFocus, count, intl, value, required } = this.props;
        const isTouched = touched && touched[this.ID];
        const errorMessage = isTouched && errors && errors[this.ID] ? errors[this.ID] : undefined;
        const label = intl.formatMessage(TranslationHelpers.getMessageDescriptor(this.LABEL_ID), {
            n: count,
        });
        const onBlur = (event: React.FocusEvent<any>) => {
            setFieldValue(this.ID, ValueFormatters.trimExtraSpaces(event.target.value));
            handleBlur(event);
        };

        return (
            <InputField
                autoComplete={`address-line${count}`}
                labelMessage={label}
                type="text"
                id={this.ID}
                maxLength={this.MAX_LENGTH}
                name={this.ID}
                value={value}
                error={errorMessage}
                autoFocus={autoFocus}
                required={required}
                onBlur={onBlur}
            />
        );
    }
}

export default injectIntl(AddressLineField);
